import React, { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Button } from '@mui/material'
import {
  GridActionsCellItem,
  GridColumns,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridToolbarContainer,
  MuiEvent
} from '@mui/x-data-grid'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { v4 as uuidv4 } from 'uuid'

function EditToolbar(props: EditToolbarProps) {
  const { setRows, setRowModesModel } = props

  const handleClick = () => {
    const id = uuidv4()

    setRows((oldRows) => [
      ...oldRows,
      {
        id,
        firstName: '',
        lastName: '',
        userEmail: '',
        countryCode: '',
        mobileNo: '',
        isNew: true
      }
    ])

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'firstName' }
    }))
  }

  return (
    <GridToolbarContainer>
      <Button color='primary' startIcon={<AddIcon />} onClick={handleClick}>
        Add record
      </Button>
    </GridToolbarContainer>
  )
}

export default function FulfilmentUploadDataGrid(props: ICRUDDataGrid) {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const { rows, setRows } = props

  const handleRowEditStart = (
    params: GridRowParams,
    event: MuiEvent<React.SyntheticEvent>
  ) => {
    event.defaultMuiPrevented = true
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id))
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })

    const editedRow = rows.find((row) => row.id === id)
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id))
    }
  }

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false } as IFulfilmentRow
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)))
    return updatedRow
  }

  const COLUMNS: GridColumns = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'userEmail',
      headerName: 'User Email',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'countryCode',
      headerName: 'Country Code',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'mobileNo',
      headerName: 'Mobile Number',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'action',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params) => {
        const { id } = params

        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={uuidv4()}
              icon={<SaveIcon />}
              label='Save'
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={uuidv4()}
              icon={<CancelIcon />}
              label='Cancel'
              className='textPrimary'
              onClick={handleCancelClick(id)}
              color='inherit'
            />
          ]
        }

        return [
          <GridActionsCellItem
            key={uuidv4()}
            icon={<EditIcon />}
            label='Edit'
            className='textPrimary'
            onClick={handleEditClick(id)}
            color='inherit'
          />,
          <GridActionsCellItem
            key={uuidv4()}
            icon={<DeleteIcon />}
            label='Delete'
            onClick={handleDeleteClick(id)}
            color='inherit'
          />
        ]
      }
    }
  ]

  return (
    <Box
      sx={{
        height: 500,
        width: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        },
        '& .batch-update-error': {
          backgroundColor: '#d47483'
        },
        '& .batch-update-ok': {
          backgroundColor: 'rgba(157, 255, 118, 0.49)'
        }
      }}
    >
      <MUIDataGrid
        rows={rows}
        columns={COLUMNS}
        editMode='row'
        checkboxSelection
        isRowSelectable={(params: GridRowParams) => {
          const row: IFulfilmentRow = params.row

          return !!(
            row.firstName &&
            row.lastName &&
            row.userEmail &&
            row.countryCode &&
            row.mobileNo
          )
        }}
        rowModesModel={rowModesModel}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        components={{
          Toolbar: EditToolbar
        }}
        componentsProps={{
          toolbar: { setRows, setRowModesModel }
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Box>
  )
}

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void
}

interface ICRUDDataGrid {
  rows: IFulfilmentRow[]
  setRows: (rows: IFulfilmentRow[]) => void
}

interface IFulfilmentRow {
  id: string
  firstName: string
  lastName: string
  userEmail: string
  countryCode: string
  mobileNo: string
  isNew: boolean
}
